import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { BadgeCheck, BookOpenText, ExternalLink, Globe, Highlighter, SquarePen } from 'lucide-react';

import { AppShell } from '@/components/AppShell';
import { HighlightCardSkeleton, HighlightList } from '@/components/HighlightList';
import { HighlightComposer } from '@/components/HighlightComposer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useHighlightsOfSource } from '@/hooks/useHighlights';
import { urlMatchCandidates } from '@/lib/normalizeUrl';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

/** Highlights of a plain web URL (kind 9802 with an `r` tag). */
export function WebPage() {
  const params = useParams<{ url: string }>();
  const url = sanitizeUrl(params.url ?? '');
  const [composerOpen, setComposerOpen] = useState(false);

  // Match both the raw and canonical URL forms, since publishers vary.
  const { highlights, isPending } = useHighlightsOfSource({ r: url ? urlMatchCandidates(url) : undefined });

  useSeoMeta({
    title: url ? `${new URL(url).hostname} — glowmark` : 'Web highlights — glowmark',
    description: url ? `Passages readers highlighted on ${new URL(url).hostname}.` : 'Web highlights on glowmark.',
  });

  if (!url) {
    return (
      <AppShell>
        <div className="container max-w-2xl py-16">
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="text-muted-foreground max-w-sm mx-auto">
                That doesn't look like a valid URL.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  const parsed = new URL(url);

  return (
    <AppShell>
      <div className="container max-w-2xl py-8 space-y-6">
        {/* Page header */}
        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground border border-border/60">
              <Globe className="size-6" />
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="font-semibold truncate">{parsed.hostname}</h1>
              <p className="text-xs text-muted-foreground truncate">{url}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button variant="secondary" size="sm" className="h-8" asChild>
                <Link to={`/read/${encodeURIComponent(url)}`}>
                  <BookOpenText className="size-4" />
                  Read
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setComposerOpen(true)}
                aria-label="Highlight this page"
                title="Highlight this page"
              >
                <SquarePen className="size-4" />
              </Button>
              <Button variant="ghost" size="icon" asChild>
                <a href={url} target="_blank" rel="noopener noreferrer" aria-label="Open original page" title="Open original page">
                  <ExternalLink className="size-4" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Author onboarding — this page is the invite */}
        <Card className="border-dashed">
          <CardContent className="p-4 space-y-2 text-xs text-muted-foreground">
            <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
              <BadgeCheck className="size-4 text-primary" />
              Is this your page?
            </p>
            <p className="leading-relaxed">
              Declare your Nostr identity with one tag in the page <code>&lt;head&gt;</code> and
              highlights of your work will credit you automatically — zaps from readers pay you
              directly:
            </p>
            <pre className="bg-muted border border-border/60 rounded-md p-2 overflow-x-auto select-all text-[11px]">
              {'<link rel="me" href="nostr:npub1…" />'}
            </pre>
            <p className="leading-relaxed">
              Then highlight a passage with the pen button above — glowmark detects the tag on its
              own.
            </p>
          </CardContent>
        </Card>

        {/* Highlights of the page */}
        <section className="space-y-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold px-1">
            <Highlighter className="size-4.5 text-primary" />
            Highlights
            {highlights.length > 0 && (
              <Badge variant="secondary" className="ml-1">{highlights.length}</Badge>
            )}
          </h2>

          {isPending ? (
            <div className="space-y-4">
              <HighlightCardSkeleton />
              <HighlightCardSkeleton />
            </div>
          ) : highlights.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 px-8 text-center space-y-4">
                <p className="text-muted-foreground max-w-sm mx-auto">
                  No one has highlighted this page yet. Be the first — highlight a passage and
                  link it here.
                </p>
                <Button variant="secondary" onClick={() => setComposerOpen(true)}>
                  <SquarePen className="size-4" />
                  Highlight this page
                </Button>
              </CardContent>
            </Card>
          ) : (
            <HighlightList highlights={highlights} />
          )}
        </section>
      </div>

      <HighlightComposer
        open={composerOpen}
        onOpenChange={setComposerOpen}
        source={{ type: 'web' }}
        initialUrl={url}
      />
    </AppShell>
  );
}
